import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Switch,
  TextField,
  Typography,
  Alert,
  Chip,
  Stack,
  InputAdornment,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import { CurrencyRupee } from '@mui/icons-material';
import adminService, { CityServiceRate } from '../services/adminService';
import { ServiceSegment, ServiceCategory, ServiceRate } from '../types/services';
import { PageHeader, SectionCard, EmptyState } from '../components/common';
import { useAdmin } from '../contexts/AdminContext';

/**
 * A city_admin's view of the service catalog (requirement #4).
 *
 * Deliberately NOT the same component as ServicesPricing.tsx: that page is
 * the super_admin's full CRUD over the master catalog (create/edit/delete
 * segments, categories, rates — plus payment-gateway credentials, which a
 * city admin has no business touching at all). Reusing it and hiding pieces
 * risks a hidden button becoming a reachable one; a separate, narrower page
 * can only ever do the two things a city admin is allowed to do: turn a
 * master rate on or off for their city, and set a price override.
 */
const CityCatalog: React.FC = () => {
  const { effectiveCityId } = useAdmin();
  const [segments, setSegments] = useState<ServiceSegment[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [rates, setRates] = useState<ServiceRate[]>([]);
  const [overrides, setOverrides] = useState<Record<string, CityServiceRate>>({});
  const [loading, setLoading] = useState(true);
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});

  const cityId = effectiveCityId;

  useEffect(() => {
    if (!cityId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const [segmentList, categoryList, rateList, overrideMap] = await Promise.all([
          adminService.getAllSegments(),
          adminService.getAllCategories(),
          adminService.getAllServiceRates(),
          adminService.getCityServiceRates(cityId),
        ]);
        setSegments(segmentList);
        setCategories(categoryList);
        setRates(rateList);
        setOverrides(overrideMap);
      } finally {
        setLoading(false);
      }
    })();
  }, [cityId]);

  const categoriesBySegment = useMemo(() => {
    const map: Record<string, ServiceCategory[]> = {};
    categories.forEach((c) => {
      (map[c.segmentId] ||= []).push(c);
    });
    return map;
  }, [categories]);

  const ratesByCategory = useMemo(() => {
    const map: Record<string, ServiceRate[]> = {};
    rates.forEach((r) => {
      (map[r.categoryId] ||= []).push(r);
    });
    return map;
  }, [rates]);

  const handleToggle = async (rate: ServiceRate, isEnabled: boolean) => {
    if (!cityId) return;
    // Optimistic — this list can be long, and a round trip per switch flip
    // reads as broken on a slow connection.
    setOverrides((prev) => ({
      ...prev,
      [rate.id]: {
        ...(prev[rate.id] || {
          id: `${cityId}_${rate.id}`,
          cityId,
          rateId: rate.id,
          isEnabled: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        }),
        isEnabled,
      },
    }));
    try {
      await adminService.setCityServiceRate(cityId, rate.id, { isEnabled });
    } catch {
      // revert on failure
      setOverrides((prev) => ({ ...prev, [rate.id]: { ...prev[rate.id], isEnabled: !isEnabled } }));
    }
  };

  const handlePriceCommit = async (rate: ServiceRate) => {
    if (!cityId) return;
    const draft = priceDrafts[rate.id];
    if (draft === undefined) return;
    const value = draft.trim() === '' ? undefined : Number(draft);
    if (value !== undefined && (!Number.isFinite(value) || value < 0)) return;
    try {
      await adminService.setCityServiceRate(cityId, rate.id, { overridePrice: value });
      setOverrides((prev) => ({
        ...prev,
        [rate.id]: {
          ...(prev[rate.id] || {
            id: `${cityId}_${rate.id}`,
            cityId,
            rateId: rate.id,
            isEnabled: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          }),
          overridePrice: value,
        },
      }));
    } finally {
      setPriceDrafts((prev) => {
        const next = { ...prev };
        delete next[rate.id];
        return next;
      });
    }
  };

  if (!cityId) {
    return (
      <Box>
        <PageHeader title="Services & Pricing" subtitle="Turn master services on for your city and set local rates" />
        <Alert severity="warning">
          Your admin account has no city assigned — contact a super admin to get one assigned before you can manage
          the catalog.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Services & Pricing"
        subtitle="Turn master services on for your city and set local rates — segments, categories and services themselves are managed by a super admin"
      />

      {loading ? null : segments.length === 0 ? (
        <EmptyState title="No services in the master catalog yet" description="A super admin sets these up." />
      ) : (
        <Stack spacing={3}>
          {segments
            .filter((s) => s.isActive)
            .map((segment) => {
              const segmentCategories = (categoriesBySegment[segment.id] || []).filter((c) => c.isActive);
              if (segmentCategories.length === 0) return null;
              return (
                <SectionCard key={segment.id} title={segment.name} subtitle={segment.description}>
                  <Stack spacing={2.5}>
                    {segmentCategories.map((category) => {
                      const categoryRates = (ratesByCategory[category.id] || []).filter((r) => r.isActive);
                      if (categoryRates.length === 0) return null;
                      return (
                        <Box key={category.id}>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                            {category.name}
                          </Typography>
                          <Grid container spacing={2}>
                            {categoryRates.map((rate) => {
                              const override = overrides[rate.id];
                              const isEnabled = override?.isEnabled !== false; // default on until a city admin turns it off
                              const priceValue =
                                priceDrafts[rate.id] ??
                                (override?.overridePrice !== undefined ? String(override.overridePrice) : '');
                              return (
                                <Grid item xs={12} sm={6} md={4} key={rate.id}>
                                  <Box
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: isEnabled ? 'action.hover' : 'transparent',
                                      opacity: isEnabled ? 1 : 0.6,
                                    }}
                                  >
                                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                          {rate.serviceName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          Master price: ₹{rate.basePrice}
                                          {rate.unit ? ` / ${rate.unit}` : ''}
                                        </Typography>
                                      </Box>
                                      <Switch
                                        size="small"
                                        checked={isEnabled}
                                        onChange={(e) => handleToggle(rate, e.target.checked)}
                                      />
                                    </Stack>
                                    {isEnabled && (
                                      <TextField
                                        size="small"
                                        fullWidth
                                        sx={{ mt: 1.5 }}
                                        placeholder={`${rate.basePrice}`}
                                        label="Price override (optional)"
                                        value={priceValue}
                                        onChange={(e) =>
                                          setPriceDrafts((prev) => ({ ...prev, [rate.id]: e.target.value }))
                                        }
                                        onBlur={() => handlePriceCommit(rate)}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <CurrencyRupee fontSize="small" />
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    )}
                                    {override?.overridePrice !== undefined && isEnabled && (
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        sx={{ mt: 1 }}
                                        label={`Your price: ₹${override.overridePrice}`}
                                      />
                                    )}
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      );
                    })}
                  </Stack>
                </SectionCard>
              );
            })}
        </Stack>
      )}
    </Box>
  );
};

export default CityCatalog;
